import React, { useRef, useState } from 'react';
import { FieldStructure, CustomFieldStructure } from '@mercell/form-react';
import { FormDefinitionItem } from '../../types/generated/formDefinitionItem';
import { TFunction } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getFormLanguage } from '../storageService/sessionService';
import { uploadDocumentFile } from '../../apiCalls/uploadFile';
import { toast } from 'react-toastify';
import { ProgressBarRound32, Upload32 } from '@carbon/icons-react';
import Button from '@mercell/button-react';

const FileUploaderComponent = (props: any) => {
    const {
        formId,
        arrayElementId,
        parentArrayElementId,
    } = useParams<{
        formId: string;
        arrayElementId?: string;
        parentArrayElementId?: string;
    }>();
    const language = getFormLanguage();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { t } = props;
    const relativeFieldPath = props.input?.name;
    const [documentId, setDocumentId] = useState<string | undefined>();

    const handleFileUpload = async (files: FileList | null) => {
        if (!files || files.length === 0) return;

        if (!formId || !language || !relativeFieldPath) {
            const errorMessage = t('form-content:FileUploadMissingParameters');
            toast.error(errorMessage);
            return;
        }

        const file = files[0];
        const toastId = `upload-${file.name}`;
        const loadingMessage = t('form-content:FileUploadInProgress', {
            fileName: file.name,
        });
        toast.loading(loadingMessage, {
            toastId,
            icon: (
                <ProgressBarRound32 className=" text-white animate-spin" />
            ),
        });

        try {
            const result = await uploadDocumentFile(formId, language, file, {
                arrayElementId,
                parentArrayElementId,
                relativeFieldPath,
            });

            if (result.errors) {
                const errorMessage =
                    result.errors || t('form-content:FileUploadError');
                toast.update(toastId, {
                    render: errorMessage,
                    type: 'error',
                    isLoading: false,
                    autoClose: 5000,
                });
            } else {
                toast.update(toastId, {
                    type: 'success',
                    isLoading: false,
                    autoClose: 100,
                });
                toast.success(toastId, {
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                });
                console.log('File uploaded:', {
                    documentId: result.documentId,
                });

                setDocumentId(result.documentId);
            }
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.errors ||
                error?.message ||
                t('form-content:FileUploadError');
            toast.update(toastId, {
                render: errorMessage,
                type: 'error',
                isLoading: false,
                autoClose: 5000,
            });
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="pt-6 pl-14 print-field">
            {documentId ? (
                <div>
                    <p className="text-sm text-mirage mb-2">
                        {t('form-content:FileUploadedSuccessfully')}
                    </p>
                    <span className="text-mirage">
                        {t('form-content:DocumentId')}: {documentId}
                    </span>
                </div>
            ) : (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload(e.target.files)}
                    />
                    <Button
                        scheme="secondary"
                        iconSettings={{ Icon: Upload32 }}
                        onClick={handleButtonClick}
                    >
                        {t('form-content:UploadFile')}
                    </Button>
                </>
            )}
        </div>
    );
};

export const mapFileUploaderField = (
    formDefinition: FormDefinitionItem,
    genericFieldStructure: FieldStructure,
    t: TFunction<string, unknown>
): CustomFieldStructure => ({
    ...genericFieldStructure,
    type: 'custom',
    render: FileUploaderComponent,
    label: formDefinition.label,
    labelProperties: formDefinition.labelStyle,
    t,
});
